/**
 * Visual playing card component with SVG face card art,
 * standard pip layouts, and ornate ace design.
 */
import { KingArt, QueenArt, JackArt, AceArt, PipLayout } from './FaceCardArt';

interface CardProps {
  suit: string;
  rank: number;
  faceDown?: boolean;
  size?: 'sm' | 'md' | 'lg';
  playable?: boolean;
  disabled?: boolean;
  highlighted?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const SUIT_SYMBOL: Record<string, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

const SUIT_COLOR: Record<string, string> = {
  spades: '#1a1a2e',
  hearts: '#c0392b',
  diamonds: '#c0392b',
  clubs: '#1a1a2e',
};

const SUIT_NAMES: Record<string, string> = {
  spades: 'Spades',
  hearts: 'Hearts',
  diamonds: 'Diamonds',
  clubs: 'Clubs',
};

const RANK_LABEL: Record<number, string> = {
  1: 'A',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  6: '6',
  7: '7',
  8: '8',
  9: '9',
  10: '10',
  11: 'J',
  12: 'Q',
  13: 'K',
  14: 'A',
};

function FaceCardContent({ suit, rank, color }: { suit: string; rank: number; color: string }) {
  if (rank === 13) return <KingArt suit={suit} color={color} />;
  if (rank === 12) return <QueenArt suit={suit} color={color} />;
  if (rank === 11) return <JackArt suit={suit} color={color} />;
  return null;
}

export function Card({
  suit,
  rank,
  faceDown = false,
  size = 'md',
  playable = false,
  disabled = false,
  highlighted = false,
  onClick,
  className = '',
  style,
}: CardProps) {
  const symbol = SUIT_SYMBOL[suit] ?? suit;
  const color = SUIT_COLOR[suit] ?? '#1a1a2e';
  const label = RANK_LABEL[rank] ?? String(rank);
  const isFace = rank >= 11 && rank <= 13;
  const isAce = rank === 14 || rank === 1;
  const isNumber = rank >= 2 && rank <= 10;

  const classes = [
    'card-visual',
    `card-${size}`,
    faceDown ? 'card-back' : 'card-face',
    playable ? 'card-playable' : '',
    disabled ? 'card-disabled' : '',
    highlighted ? 'card-highlighted' : '',
    isFace ? 'card-is-face' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (faceDown) {
    return (
      <div className={classes} style={style}>
        <div className="card-back-pattern" />
      </div>
    );
  }

  const suitName = SUIT_NAMES[suit] ?? suit;
  const cardAriaLabel = faceDown
    ? 'Face-down card'
    : `${label} of ${suitName}${playable ? ' — playable' : ''}`;

  return (
    <button
      className={classes}
      style={{ ...style, '--card-color': color } as React.CSSProperties}
      onClick={onClick}
      disabled={disabled || !playable}
      type="button"
      aria-label={cardAriaLabel}
    >
      <span className="card-corner card-corner-top">
        <span className="card-rank">{label}</span>
        <span className="card-suit-small">{symbol}</span>
      </span>

      <div className="card-content">
        {isNumber && <PipLayout rank={rank} suit={suit} color={color} />}
        {isFace && <FaceCardContent suit={suit} rank={rank} color={color} />}
        {isAce && <AceArt suit={suit} color={color} />}
      </div>

      <span className="card-corner card-corner-bottom">
        <span className="card-rank">{label}</span>
        <span className="card-suit-small">{symbol}</span>
      </span>
    </button>
  );
}

export function CardBack({
  size = 'sm',
  className = '',
  style,
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: React.CSSProperties;
}) {
  return <Card suit="spades" rank={1} faceDown size={size} className={className} style={style} />;
}

export function formatCardText(card: { suit: string; rank: number }): string {
  const suit = SUIT_SYMBOL[card.suit] ?? card.suit;
  const rank = RANK_LABEL[card.rank] ?? String(card.rank);
  return `${rank}${suit}`;
}
